import { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';
import { Flex } from 'antd-v5';
import { getIdMap } from 'ontraccr-common/lib/Common';

import FilteredUserSelector from '../common/inputs/FilteredUserSelector';
import WhoseScheduledDrawer from './WhoseScheduledDrawer';
import {
  calcProjectCoords,
  calcUserDistances,
  getShiftConflictsByKey,
} from './scheduleHelpers';

export default function ScheduleUserSelector({
  value,
  onChange,
  onUserClick = () => {},
  dateRange,
  users,
  project,
  showLabels = false,
  disabled = false,
  assignedUsers,
  shiftId,
}) {
  const shiftUsers = useSelector((state) => state.schedule.shiftUsers);
  const shiftMap = useSelector((state) => state.schedule.shiftMap);
  const categories = useSelector((state) => state.schedule.categories);
  const projects = useSelector((state) => state.projects.projects);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const categoryMap = useMemo(() => getIdMap(categories), [categories]);

  const [startDate, endDate] = useMemo(() => {
    const [startTs, endTs] = dateRange ?? [];
    return [
      startTs ? DateTime.fromMillis(startTs) : startTs,
      endTs ? DateTime.fromMillis(endTs) : endTs,
    ];
  }, [dateRange]);

  const conflictsByUserId = useMemo(() => getShiftConflictsByKey({
    dateRange,
    excludeShiftId: shiftId,
    shiftIdsByKey: shiftUsers,
    shiftMap,
    projectMap,
    categoryMap,
  }), [dateRange, shiftId, shiftUsers, shiftMap, projectMap, categoryMap]);

  const scheduledUserIds = useMemo(() => new Set(assignedUsers), [assignedUsers]);

  const onUserRemove = useCallback((removedUserId) => {
    onChange(assignedUsers.filter((userId) => userId !== removedUserId));
  }, [assignedUsers, onChange]);

  const projectCoords = useMemo(
    () => calcProjectCoords(project),
    [project],
  );

  const userDistances = useMemo(
    () => calcUserDistances(projectCoords, users),
    [projectCoords, users],
  );

  const sortedUsers = useMemo(() => {
    const arr = [...users];
    arr.sort((a, b) => {
      const da = userDistances[a.id];
      const db = userDistances[b.id];
      if (da != null && db != null) return da - db;
      if (da != null) return -1;
      if (db != null) return 1;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [users, userDistances]);

  const usersForSelect = useMemo(() => (
    sortedUsers.map((u) => {
      return {
        ...u,
        distanceKm: userDistances[u.id] ?? null,
      };
    })
  ), [sortedUsers, userDistances]);
  return (
    <Flex vertical gap={10} align="start">
      {dateRange?.length && !disabled ? (
        <WhoseScheduledDrawer
          onClick={users && users.length > 0 ? onUserClick : null}
          users={users}
          startDate={startDate}
          endDate={endDate}
          width={600}
          scheduledUserIds={scheduledUserIds}
          showUsersScheduledToShift
          onUserRemove={onUserRemove}
        />
      ) : null}
      <FilteredUserSelector
        users={usersForSelect}
        value={value}
        onChange={onChange}
        showLabels={showLabels}
        disabled={disabled}
        conflictsByUserId={conflictsByUserId}
      />
    </Flex>
  );
}

ScheduleUserSelector.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onUserClick: PropTypes.func.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.number),
  users: PropTypes.arrayOf(PropTypes.shape({})),
  project: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    address: PropTypes.string,
  }),
  showLabels: PropTypes.bool,
  disabled: PropTypes.bool,
  assignedUsers: PropTypes.arrayOf(PropTypes.string),
  shiftId: PropTypes.string,
};

ScheduleUserSelector.defaultProps = {
  value: [],
  dateRange: [],
  users: [],
  project: null,
  showLabels: false,
  disabled: false,
  assignedUsers: [],
  shiftId: null,
};
